import { useState } from "react";
import axios from "axios";
import { Layout } from "@/components/Layout";
import { Users, Check, Loader2, AlertCircle, Send, FileText } from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AssociarsiPage() {
  const [form, setForm] = useState({
    name: "",
    surname: "",
    email: "",
    phone: "",
    birth_date: "",
    address: "",
    motivation: "",
    accept_statuto: false,
  });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const onField = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.accept_statuto) {
      setError("Devi accettare lo Statuto per procedere.");
      return;
    }
    setStatus("loading");
    try {
      await axios.post(`${API}/soci`, {
        ...form,
        birth_date: form.birth_date || null,
        address: form.address || null,
        motivation: form.motivation || null,
      });
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setError(err?.response?.data?.detail || "Errore. Riprova più tardi.");
    }
  };

  return (
    <Layout>
      <section className="bg-[#1f2d22] text-white py-16 sm:py-20" data-testid="associarsi-header">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-4 py-1.5 mb-5">
            <Users className="w-3.5 h-3.5 text-[#E8C9A0]" />
            <span className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.22em] text-[#E8C9A0]">
              Diventa socio
            </span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight">
            Associati a <span className="italic font-light text-[#E8C9A0]">Per Asciano.</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-white/75 max-w-2xl leading-relaxed">
            L'adesione all'associazione è <strong className="text-white">gratuita</strong>. Compila il form e verrai
            contattato per perfezionare l'iscrizione secondo lo Statuto.
          </p>
        </div>
      </section>

      <section className="py-14 sm:py-20 bg-[#FBF9F6]">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12">
          {status === "success" ? (
            <div data-testid="associarsi-success" className="rounded-3xl bg-[#2E4F3B] text-white p-10 sm:p-14 text-center">
              <div className="w-16 h-16 rounded-full bg-white/15 flex items-center justify-center mx-auto mb-6">
                <Check className="w-8 h-8 text-[#E8C9A0]" />
              </div>
              <h2 className="font-display text-3xl font-black">Richiesta inviata.</h2>
              <p className="mt-4 text-white/80 max-w-xl mx-auto leading-relaxed">
                Grazie! Verrai contattato a breve per perfezionare la tua adesione all'associazione.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="associarsi-form" className="bg-white border-2 border-[#E5E1D8] rounded-3xl p-7 sm:p-10 space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <FormField name="name" label="Nome *" value={form.name} onChange={onField} required testid="socio-name" />
                <FormField name="surname" label="Cognome *" value={form.surname} onChange={onField} required testid="socio-surname" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <FormField name="email" type="email" label="Email *" value={form.email} onChange={onField} required testid="socio-email" />
                <FormField name="phone" type="tel" label="Telefono *" value={form.phone} onChange={onField} required testid="socio-phone" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <FormField name="birth_date" type="date" label="Data di nascita" value={form.birth_date} onChange={onField} testid="socio-birth" />
                <FormField name="address" label="Residenza" value={form.address} onChange={onField} placeholder="Via, città" testid="socio-address" />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[#5C5C5C] mb-2">Perché vuoi associarti? (facoltativo)</label>
                <textarea
                  name="motivation"
                  rows={4}
                  value={form.motivation}
                  onChange={onField}
                  data-testid="socio-motivation"
                  placeholder="Raccontaci brevemente cosa ti spinge ad aderire all'associazione…"
                  className="w-full bg-[#FBF9F6] border-2 border-[#E5E1D8] rounded-xl px-4 py-3 text-[#1A1A1A] focus:outline-none focus:border-[#2E4F3B] focus:ring-2 focus:ring-[#2E4F3B]/10 transition-all resize-none"
                />
              </div>

              <div className="rounded-xl bg-[#FBF9F6] border-2 border-[#E5E1D8] p-5">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="accept_statuto"
                    checked={form.accept_statuto}
                    onChange={onField}
                    data-testid="socio-statuto"
                    required
                    className="mt-0.5 w-5 h-5 rounded border-2 border-[#2E4F3B]/40 text-[#2E4F3B] focus:ring-[#2E4F3B] cursor-pointer"
                  />
                  <span className="text-sm text-[#1A1A1A] leading-relaxed">
                    Dichiaro di aver letto e di accettare lo{" "}
                    <Link to="/trasparenza" className="font-bold text-[#2E4F3B] underline inline-flex items-center gap-1">
                      Statuto dell'Associazione <FileText className="w-3 h-3" />
                    </Link>
                    .
                  </span>
                </label>
              </div>

              <button
                type="submit"
                disabled={status === "loading"}
                data-testid="socio-submit"
                className="w-full inline-flex items-center justify-center gap-3 rounded-full bg-[#2E4F3B] hover:bg-[#1f3828] text-white font-bold text-base px-8 py-4 transition-all disabled:opacity-60 shadow-lg"
              >
                {status === "loading" ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Invio in corso…
                  </>
                ) : (
                  <>
                    Invia richiesta di adesione
                    <Send className="w-4 h-4" />
                  </>
                )}
              </button>

              {error && (
                <div data-testid="socio-error" className="flex items-start gap-3 bg-[#C15C3D]/10 border border-[#C15C3D]/30 rounded-xl p-3">
                  <AlertCircle className="w-4 h-4 text-[#C15C3D] shrink-0 mt-0.5" />
                  <p className="text-xs text-[#1A1A1A] leading-relaxed">{error}</p>
                </div>
              )}

              <p className="text-[11px] text-[#5C5C5C] text-center leading-relaxed">
                Adesione gratuita · I tuoi dati saranno usati esclusivamente per la gestione associativa.
              </p>
            </form>
          )}
        </div>
      </section>
    </Layout>
  );
}

const FormField = ({ name, label, value, onChange, type = "text", required, placeholder, testid }) => (
  <div>
    <label htmlFor={name} className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[#5C5C5C] mb-2">
      {label}
    </label>
    <input
      id={name}
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full bg-[#FBF9F6] border-2 border-[#E5E1D8] rounded-xl px-4 py-3 text-[#1A1A1A] placeholder:text-[#9A9A9A] focus:outline-none focus:border-[#2E4F3B] focus:ring-2 focus:ring-[#2E4F3B]/10 transition-all"
    />
  </div>
);
